import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { toast } from "@shared/ui/use-toast";
import { Edit, FileText, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteSystem } from "../../hooks";

export const QuickActionsSidebar: React.FC<{ data: any }> = ({ data }) => {
  const navigate = useNavigate();

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [systemToDelete, setSystemToDelete] = useState<any>(null);

  const handleDeleteClick = () => {
    setSystemToDelete(data);
    setShowDeleteDialog(true);
  };

  const { mutate: deleteSystemMutation, isPending: isDeletingSystem } =
    useDeleteSystem();

  const handleConfirmDelete = () => {
    if (systemToDelete) {
      logEvent("System Delete Confirmed", {
        systemId: systemToDelete.id,
      });
      deleteSystemMutation(
        { systemId: systemToDelete.id },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: `"${systemToDelete.systemName}" has been deleted successfully.`,
            });
            setShowDeleteDialog(false);
            setSystemToDelete(null);
            navigate(`/ax/asset-registry/systems`);
          },
          onError: (error) => {
            toast({
              title: "Delete Failed",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    }
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setSystemToDelete(null);
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          <p className="text-sm text-gray-600">
            Common System management tasks
          </p>
        </CardHeader>
        <CardContent className="space-y-3 flex flex-col">
          <Button
            variant="outline"
            className="w-full justify-start p-2"
            tooltip={"Edit System"}
            onClick={() =>
              navigate(`/ax/asset-registry/systems/edit/${data?.id}`)
            }
          >
            <Edit className="h-4 w-4" />
            Edit System
          </Button>

          <Button
            variant="outline"
            className="w-full justify-start p-2"
            tooltip={"Create Service Order"}
            onClick={() => navigate(`/ax/service-order/landing/create`)}
          >
            <FileText className="h-4 w-4" />
            <span className="truncate text-left">Create Service Order</span>
          </Button>

          <Button
            variant="outline"
            className="w-full justify-start p-2"
            tooltip={"Delete System"}
            onClick={handleDeleteClick}
          >
            <Trash2 className="h-4 w-4" />
            <span className="truncate text-left">Delete</span>
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete System"
        itemName={data?.systemName}
        description={`Are you sure you want to delete the system "${data?.systemName}" (ID: ${data?.systemNo})? This will permanently remove the system and all its associated data. This action cannot be undone.`}
        isLoading={isDeletingSystem}
      />
    </div>
  );
};
